import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

import { AthleteCard } from './AthleteCard';
import { RosterFilterBar, type SportOption } from './RosterFilterBar';
import type { Athlete, RosterPage } from './types';

const MOBILE_BREAKPOINT = '(max-width: 900px)';
const ROW_GAP_DESKTOP = 8;
const ROW_GAP_MOBILE = 8;
const CARD_ASPECT_W = 350;
const CARD_ASPECT_H = 467;
const PAGE_SIZE = 24;
const PREFETCH_THRESHOLD_ROWS = 2;
const API_URL = '/api/public/roster';
const SEARCH_URL = '/api/public/roster/search';
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

function getColumnCount(): number {
    if (typeof window === 'undefined') { return 4; }
    return window.matchMedia(MOBILE_BREAKPOINT).matches ? 2 : 4;
}

export function RosterGrid({ initial }: { initial: RosterPage }) {
    const [athletes, setAthletes] = useState<Athlete[]>(initial.athletes);
    const [cursor, setCursor] = useState<string | null>(initial.next_cursor);
    const [hasMore, setHasMore] = useState<boolean>(initial.has_more);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [columnCount, setColumnCount] = useState<number>(() => getColumnCount());
    const [containerWidth, setContainerWidth] = useState(0);
    const [scrollMargin, setScrollMargin] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedSports, setSelectedSports] = useState<string[]>([]);
    // null = not searching (show the curated/paginated roster); array = server search results.
    const [searchResults, setSearchResults] = useState<Athlete[] | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    const toggleSport = useCallback((name: string) => {
        setSelectedSports((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
    }, []);
    const clearSports = useCallback(() => setSelectedSports([]), []);

    // Sports present on the roster (deduped by name), each with its icon, for the filter list.
    const sports = useMemo<SportOption[]>(() => {
        const byName = new Map<string, string | null>();
        for (const a of athletes) {
            if (a.sport && !byName.has(a.sport)) { byName.set(a.sport, a.sport_icon ?? null); }
        }
        return [...byName.entries()].map(([name, icon]) => ({ name, icon }));
    }, [athletes]);

    // When a query is active, results come from the server (entire athlete DB,
    // beyond the curated roster); otherwise we show the loaded/paginated roster.
    const baseAthletes = searchResults !== null ? searchResults : athletes;

    const filteredAthletes = useMemo<Athlete[]>(() => {
        if (selectedSports.length === 0) { return baseAthletes; }
        return baseAthletes.filter((a) => selectedSports.includes(a.sport ?? ''));
    }, [baseAthletes, selectedSports]);

    const searchActive = query.trim().length >= SEARCH_MIN_CHARS;
    const isFiltering = searchActive || selectedSports.length > 0;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inFlightRef = useRef(false);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    useEffect(() => {
        const q = query.trim();
        if (q.length < SEARCH_MIN_CHARS) {
            setSearchResults(null);
            setSearchLoading(false);
            setSearchError(null);
            return;
        }
        setSearchLoading(true);
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            const params = new URLSearchParams({ q });
            fetch(`${SEARCH_URL}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            })
                .then((res) => {
                    if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
                    return res.json() as Promise<{ athletes: Athlete[] }>;
                })
                .then((data) => {
                    setSearchResults(data.athletes);
                    setSearchError(null);
                })
                .catch((e: unknown) => {
                    if (controller.signal.aborted) { return; }
                    setSearchResults([]);
                    setSearchError(e instanceof Error ? e.message : 'failed to search');
                })
                .finally(() => {
                    if (!controller.signal.aborted) { setSearchLoading(false); }
                });
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [query]);

    useEffect(() => {
        const mql = window.matchMedia(MOBILE_BREAKPOINT);
        const update = () => setColumnCount(mql.matches ? 2 : 4);
        update();
        mql.addEventListener('change', update);
        return () => mql.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (scrolled) { return; }
        const onScroll = () => {
            if (window.scrollY > 4) { setScrolled(true); }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [scrolled]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) { return; }
        const updateOffset = () => {
            const rect = el.getBoundingClientRect();
            setScrollMargin(rect.top + window.scrollY);
        };
        const ro = new ResizeObserver(() => {
            setContainerWidth(el.clientWidth);
            updateOffset();
        });
        ro.observe(el);
        setContainerWidth(el.clientWidth);
        updateOffset();
        window.addEventListener('resize', updateOffset);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateOffset);
        };
    }, []);

    const gap = columnCount === 2 ? ROW_GAP_MOBILE : ROW_GAP_DESKTOP;

    const rowHeight = useMemo(() => {
        if (containerWidth === 0) { return CARD_ASPECT_H; }
        const cardWidth = (containerWidth - gap * (columnCount - 1)) / columnCount;
        return cardWidth * (CARD_ASPECT_H / CARD_ASPECT_W) + gap;
    }, [containerWidth, columnCount, gap]);

    const rows = useMemo(() => {
        const result: Athlete[][] = [];
        for (let i = 0; i < filteredAthletes.length; i += columnCount) {
            result.push(filteredAthletes.slice(i, i + columnCount));
        }
        return result;
    }, [filteredAthletes, columnCount]);

    const virtualizer = useWindowVirtualizer({
        count: rows.length,
        estimateSize: () => rowHeight,
        overscan: 4,
        scrollMargin,
    });

    useEffect(() => {
        virtualizer.measure();
    }, [rowHeight, virtualizer]);

    const fetchNext = useCallback(async () => {
        if (inFlightRef.current || !hasMore || !cursor) { return; }
        inFlightRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ cursor, limit: String(PAGE_SIZE) });
            const res = await fetch(`${API_URL}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
            const data: RosterPage = await res.json();
            if (!mountedRef.current) { return; }
            setAthletes((prev) => [...prev, ...data.athletes]);
            setCursor(data.next_cursor);
            setHasMore(data.has_more);
        } catch (e) {
            if (!mountedRef.current) { return; }
            setError(e instanceof Error ? e.message : 'failed to load');
        } finally {
            inFlightRef.current = false;
            if (mountedRef.current) { setLoading(false); }
        }
    }, [cursor, hasMore]);

    const handleRetry = useCallback(() => {
        setError(null);
        fetchNext();
    }, [fetchNext]);

    const virtualItems = virtualizer.getVirtualItems();
    useEffect(() => {
        if (error || searchActive) { return; }
        const last = virtualItems[virtualItems.length - 1];
        if (!last) { return; }
        if (last.index >= rows.length - PREFETCH_THRESHOLD_ROWS) {
            fetchNext();
        }
    }, [virtualItems, rows.length, fetchNext, error, searchActive]);

    return (
        <>
        <section className="roster-grid-section" aria-label="Athlete roster">
            {athletes.length > 0 && (
                <RosterFilterBar
                    sports={sports}
                    query={query}
                    selectedSports={selectedSports}
                    onQueryChange={setQuery}
                    onToggleSport={toggleSport}
                    onClearSports={clearSports}
                />
            )}
            <div
                ref={containerRef}
                className={`roster-grid${scrolled ? ' roster-grid--scrolled' : ''}`}
                data-columns={columnCount}
            >
                <div
                    className="roster-grid__viewport"
                    style={{ height: `${virtualizer.getTotalSize()}px` }}
                >
                    {virtualItems.map((virtualRow) => {
                        const row = rows[virtualRow.index];
                        if (!row) { return null; }
                        return (
                            <div
                                key={virtualRow.key}
                                ref={virtualizer.measureElement}
                                className="roster-grid__row"
                                data-index={virtualRow.index}
                                style={{
                                    transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                                    paddingBottom: `${gap}px`,
                                }}
                            >
                                <div
                                    className="roster-grid__row-inner"
                                    style={{
                                        columnGap: `${gap}px`,
                                        animationDelay: `${Math.min(virtualRow.index * 180, 1440)}ms`,
                                    }}
                                >
                                    {row.map((athlete) => (
                                        <div key={athlete.id} className="roster-grid__cell">
                                            <AthleteCard athlete={athlete} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {searchLoading && (
                    <div className="roster-grid__status" role="status" aria-live="polite">
                        Searching...
                    </div>
                )}
                {searchError && !searchLoading && (
                    <div className="roster-grid__status roster-grid__status--error" role="alert">
                        Couldn&apos;t search athletes.
                    </div>
                )}
                {isFiltering && !searchLoading && !searchError && filteredAthletes.length === 0 && (
                    <div className="roster-grid__status" role="status">
                        No athletes match your {searchActive ? 'search' : 'filter'}.
                    </div>
                )}
                {loading && !searchActive && (
                    <div className="roster-grid__status" role="status" aria-live="polite">
                        Loading more athletes...
                    </div>
                )}
                {error && !searchActive && (
                    <div className="roster-grid__status roster-grid__status--error" role="alert">
                        Couldn&apos;t load more athletes. <button type="button" onClick={handleRetry}>Retry</button>
                    </div>
                )}
            </div>
        </section>
        </>
    );
}
